import { useGSAP } from '@gsap/react';
import gsap from 'gsap';

import { PortableText } from '@portabletext/react';
import { graphql } from 'gatsby';
import React, { useRef } from 'react';
import { ButtonLinkType } from '../../../graphql-fragments/ButtonLink';
import cloud_bottom_desktop from '../../../images/ctaModule_cloud_bottom_desktop.png';
import cloud_bottomLeft_mobile from '../../../images/ctaModule_cloud_bottom_mobile.png';
import cloud_bottom_tablet from '../../../images/ctaModule_cloud_bottom_tablet.png';
import cloud_top_desktop from '../../../images/ctaModule_cloud_top_desktop.png';
import cloud_top_mobile from '../../../images/ctaModule_cloud_top_mobile.png';
import table_desktop from '../../../images/ctaModule_table_desktop.png';
import table_mobile from '../../../images/ctaModule_table_mobile.png';
import table_tablet from '../../../images/ctaModule_table_tablet.png';
import night_table_desktop from '../../../images/night_ctaModule_table_desktop.png';
import night_table_mobile from '../../../images/night_ctaModule_table_mobile.png';
import night_table_tablet from '../../../images/night_ctaModule_table_tablet.png';
import { getComponents } from '../../../portableText';
import { RawPortableText } from '../../../types/types';
import { clsx } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import AnimatedTitle from '../AnimatedTitle';
import { useScrollLinkedClouds } from '../Background';
import ButtonLink from '../ButtonLink';
import ModuleLayout from '../ModuleLayout';
import * as styles from './CtaModule.module.scss';

export const CtaModuleFragment = graphql`
  fragment CtaModule on SanityCtaModule {
    title {
      ...LocaleStringFragment
    }
    subtitle {
      ...LocaleStringFragment
    }
    text {
      ...LocaleParagraphPortableText
    }
    button {
      ...ButtonLink
    }
  }
`;

export type CtaModuleProps = {
  title: string;
  subtitle?: string;
  text?: RawPortableText;
  button?: ButtonLinkType;
  className?: string;
};

function CtaModule(props: CtaModuleProps & CommonModuleProps): React.ReactElement {
  const { title, subtitle, text, button, className, moduleId } = props;
  const containerRef = useRef<HTMLElement | null>(null);

  useScrollLinkedClouds(`.${styles.topCloud}, .${styles.secondTopCloud}, .${styles.bottomCloud}`, {
    scope: containerRef,
  });

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      mm.add(
        {
          toTabletHorizontal: '(max-width: 959px)',
          tabletHorizontalToLaptop: '(min-width: 960px) and (max-width: 1399px)',
          fromLaptop: '(min-width: 1400px)',
        },
        context => {
          const { tabletHorizontalToLaptop, toTabletHorizontal } = context.conditions || {};

          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: `.${styles.contentWrapper}`,
              start: '25% 75%',
            } satisfies ScrollTrigger.Vars,
          });

          tl.set(`.${styles.table}`, {
            y: tabletHorizontalToLaptop ? -140 : 0,
            yPercent: toTabletHorizontal ? -25 : 0,
          });

          tl.addLabel('start');

          tl.from(`.${styles.table}`, {
            yPercent: toTabletHorizontal ? 100 : -100,
            duration: 2,
            ease: 'power1.out',
            immediateRender: false,
          });

          tl.to(`.${styles.table}`, {
            y: tabletHorizontalToLaptop ? -120 : 20,
            duration: 2,
            repeat: -1,
            yoyo: true,
            ease: 'power1.inOut',
          });

          tl.from(
            `.${styles.subtitle}, .${styles.text}, .${styles.button}`,
            {
              y: 10,
              autoAlpha: 0,
              duration: 1,
              stagger: 0.2,
              delay: 0.5,
            },
            'start',
          );
        },
      );
    },
    { scope: containerRef },
  );

  return (
    <ModuleLayout
      id={moduleId}
      ref={containerRef}
      className={clsx(className, styles.container)}
      contentClassName={clsx(styles.contentContainer)}
      childrenClassName={styles.content}
      currentModuleBgColor={props.theme}
    >
      <div className={styles.cloudsColorFilterHackWrapper}>
        <img src={cloud_top_mobile} className={clsx(styles.topCloud, styles.mobile)} alt="" />
        <img src={cloud_top_desktop} className={clsx(styles.topCloud, styles.desktop)} alt="" />
        <img
          src={cloud_top_desktop}
          className={clsx(styles.secondTopCloud, styles.desktop)}
          alt=""
        />
      </div>

      <div className={styles.contentWrapper}>
        <div className={clsx(styles.titleAndTextContainer)}>
          <AnimatedTitle title={title} trigger={styles.contentContainer} className={styles.title} />
          <div className={styles.textContainer}>
            {subtitle && <h3 className={styles.subtitle}>{subtitle}</h3>}
            {text && (
              <div className={styles.text}>
                <PortableText value={text} components={getComponents()} />
              </div>
            )}
            {button && <ButtonLink button={button} className={styles.button}></ButtonLink>}
          </div>
        </div>
        <div className={styles.imageContainer}>
          {props.theme === 'moonlit' || props.theme === 'starlit' ? (
            <>
              <img src={night_table_mobile} className={clsx(styles.table, styles.mobile)} alt="" />
              <img src={night_table_tablet} className={clsx(styles.table, styles.tablet)} alt="" />
              <img
                src={night_table_desktop}
                className={clsx(styles.table, styles.desktop)}
                alt=""
              />
            </>
          ) : (
            <>
              <img src={table_mobile} className={clsx(styles.table, styles.mobile)} alt="" />
              <img src={table_tablet} className={clsx(styles.table, styles.tablet)} alt="" />
              <img src={table_desktop} className={clsx(styles.table, styles.desktop)} alt="" />
            </>
          )}

          <div className={styles.cloudsColorFilterHackWrapper}>
            <img
              src={cloud_bottomLeft_mobile}
              className={clsx(styles.bottomCloud, styles.left, styles.mobile)}
              alt=""
            />
            <img
              src={cloud_bottom_tablet}
              className={clsx(styles.bottomCloud, styles.tablet)}
              alt=""
            />
            <img
              src={cloud_bottom_desktop}
              className={clsx(styles.bottomCloud, styles.desktop)}
              alt=""
            />
          </div>
        </div>
      </div>
    </ModuleLayout>
  );
}

export default CtaModule;
